import { 
  collection, 
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  startAt,
  endAt
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';

export class ProductService {
  static async searchProducts(filters, pageSize = 12, lastDoc = null) {
    try {
      console.log('Search started with filters:', filters);
      const queryRef = collection(db, 'products');
      let queryConstraints = [];

      // Add hidden filter
      queryConstraints.push(where('hidden', '!=', true));

      // Add status filter
      queryConstraints.push(where('status', '==', 'approved'));

      // Add filters for keyword search using searchIndex
      if (filters.keyword && filters.keyword.trim() !== '') {
        const keyword = filters.keyword.trim();
        console.log('Processing keyword:', keyword);

        // Generate search terms for the keyword
        const searchTerms = generateSearchTerms(keyword);
        console.log('Generated search terms:', searchTerms);

        if (searchTerms && searchTerms.length > 0) {
          // For Arabic text, we'll use a different approach
          if (/[\u0600-\u06FF]/.test(keyword)) {
            // If it's Arabic text, search in title and description directly
            queryConstraints.push(where('titleLower', '>=', keyword.toLowerCase()));
            queryConstraints.push(where('titleLower', '<=', keyword.toLowerCase() + '\uf8ff'));
            console.log('Added Arabic text search constraints');
          } else {
            // For non-Arabic text, use the searchIndex
            queryConstraints.push(where('searchIndex', 'array-contains-any', searchTerms));
            console.log('Added searchIndex constraint');
          }
        } else {
          console.log('No valid search terms generated, using direct text search');
          // Fallback to direct text search if no search terms generated
          queryConstraints.push(where('titleLower', '>=', keyword.toLowerCase()));
          queryConstraints.push(where('titleLower', '<=', keyword.toLowerCase() + '\uf8ff'));
        }
      }

      if (filters.category && filters.category !== '') {
        console.log('Adding category filter:', filters.category);
        queryConstraints.push(where('categoryLower', '==', filters.category.toLowerCase()));
      }

      // Add sorting
      switch (filters.sort) {
        case 'price_low':
          queryConstraints.push(orderBy('price', 'asc'));
          break;
        case 'price_high':
          queryConstraints.push(orderBy('price', 'desc'));
          break;
        case 'popular':
          queryConstraints.push(orderBy('views', 'desc'));
          break;
        default:
          queryConstraints.push(orderBy('createdAt', 'desc'));
      }

      // Add pagination
      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc));
      }
      queryConstraints.push(limit(pageSize));

      console.log('Final query constraints:', queryConstraints);

      // Execute query
      const q = query(queryRef, ...queryConstraints);
      const snapshot = await getDocs(q);

      // Sort results by relevance if keyword search
      let products = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase().trim();
        products.sort((a, b) => {
          const titleA = a.title.toLowerCase();
          const titleB = b.title.toLowerCase();

          // Exact matches come first
          if (titleA === keyword && titleB !== keyword) return -1;
          if (titleB === keyword && titleA !== keyword) return 1;

          // Starts with keyword comes second
          if (titleA.startsWith(keyword) && !titleB.startsWith(keyword)) return -1;
          if (titleB.startsWith(keyword) && !titleA.startsWith(keyword)) return 1;

          // Contains keyword comes third
          if (titleA.includes(keyword) && !titleB.includes(keyword)) return -1;
          if (titleB.includes(keyword) && !titleA.includes(keyword)) return 1;

          return 0;
        });
      }

      console.log('Search results:', {
        total: products.length,
        items: products.map(p => p.title)
      });

      return {
        products,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] || null
      };
    } catch (error) {
      console.error('Search error:', error);
      throw new Error('Failed to search products');
    }
  }

  static async addProduct(productData, images) {
    try {
      // Upload images first
      const imageUrls = await Promise.all(
        images.map(async (image) => {
          const storageRef = ref(storage, `products/${Date.now()}_${image.name}`);
          await uploadBytes(storageRef, image);
          return getDownloadURL(storageRef);
        })
      );

      // Process text fields for search
      const title = productData.title.trim();
      const description = productData.description.trim();
      const brand = productData.brand.trim();
      const model = productData.model.trim();

      // Generate search index from all relevant fields
      const searchableText = [
        title,
        description,
        brand,
        model,
        productData.category
      ].join(' ');

      // Generate search index for all searchable fields
      const searchIndex = generateSearchTerms(searchableText);

      console.log('Generated search index:', {
        searchableText,
        searchIndex
      });

      const product = {
        ...productData,
        title: title,
        description: description,
        brand: brand,
        model: model,
        // Search-related fields
        titleLower: title.toLowerCase(),
        categoryLower: productData.category.toLowerCase(),
        searchIndex: searchIndex,
        // Other fields
        images: imageUrls,
        sellerId: auth.currentUser.uid,
        hidden: false,
        status: 'pending',
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('Creating new product with search fields:', {
        title,
        titleLower: product.titleLower,
        categoryLower: product.categoryLower,
        searchIndex: product.searchIndex
      });

      const docRef = await addDoc(collection(db, 'products'), product);
      return { id: docRef.id, ...product };
    } catch (error) {
      console.error('Error adding product:', error);
      throw new Error('Failed to add product');
    }
  }

  static async getProductById(id) {
    const docRef = doc(db, 'products', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    throw new Error('Product not found');
  }

  static async updateProduct(productId, productData) {
    try {
      // First, get the current product data
      const productRef = doc(db, 'products', productId);
      const currentProduct = await getDoc(productRef);
      
      if (!currentProduct.exists()) {
        throw new Error('Product not found');
      }

      // Process text fields for search
      const title = productData.title?.trim() || '';
      const description = productData.description?.trim() || '';
      const brand = productData.brand?.trim() || '';
      const model = productData.model?.trim() || '';
      const category = productData.category?.trim() || '';
      const condition = productData.condition?.trim() || '';
      const location = productData.location?.trim() || '';

      // Generate searchable text from all relevant fields
      const searchableText = [
        title,
        description,
        brand,
        model,
        category,
        condition,
        location
      ].filter(Boolean).join(' ');

      console.log('Updating product:', {
        productId,
        oldTitle: currentProduct.data().title,
        newTitle: title,
        oldSearchIndex: currentProduct.data().searchIndex,
        searchableText
      });

      // Generate search terms
      const searchTerms = searchableText.toLowerCase()
        .split(/\s+/)
        .filter(word => word.length >= 2);

      // Generate partial matches for each word
      const searchIndex = new Set();
      searchTerms.forEach(word => {
        // Add full word
        searchIndex.add(word);
        
        // Add partial matches (minimum 3 characters)
        if (word.length > 3) {
          for (let i = 3; i <= word.length; i++) {
            searchIndex.add(word.substring(0, i));
          }
        }
      });

      const finalSearchIndex = Array.from(searchIndex);

      console.log('Generated new search index:', {
        searchTerms,
        finalSearchIndex
      });

      const updatedProduct = {
        ...productData,
        title,
        titleLower: title.toLowerCase(),
        categoryLower: category.toLowerCase(),
        searchIndex: finalSearchIndex,
        updatedAt: serverTimestamp()
      };

      await updateDoc(productRef, updatedProduct);

      console.log('Product updated successfully with new search fields:', {
        productId,
        title,
        searchIndex: finalSearchIndex
      });

      return { id: productId, ...updatedProduct };
    } catch (error) {
      console.error('Error updating product:', {
        productId,
        error: error.message
      });
      throw new Error('Failed to update product');
    }
  }

  static async deleteProduct(id) {
    const docRef = doc(db, 'products', id);
    await deleteDoc(docRef);
  }

  static async getUserProducts(userId) {
    try {
      const queryRef = collection(db, 'products');
      const q = query(
        queryRef,
        where('sellerId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user products:', error);
      throw new Error('Failed to get user products');
    }
  }

  static async getUserMessages(userId) {
    try {
      const queryRef = collection(db, 'messages');
      const q = query(
        queryRef,
        where('receiverId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user messages:', error);
      throw new Error('Failed to get user messages');
    }
  }

  static async updateAllProductsWithTitleSearch() {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const batch = writeBatch(db);
      let count = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.titleSearch && data.title) {
          batch.update(doc.ref, {
            titleSearch: data.title.toLowerCase()
          });
          count++;
        }
      });

      if (count > 0) {
        await batch.commit();
        console.log(`Updated ${count} products with titleSearch field`);
      } else {
        console.log('No products needed updating');
      }
      
      return count;
    } catch (error) {
      console.error('Error updating products:', error);
      throw error;
    }
  }

  static async toggleProductVisibility(productId) {
    try {
      if (!auth.currentUser) {
        throw new Error('You must be logged in to modify products');
      }

      const productRef = doc(db, 'products', productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }

      // Check if user owns the product
      if (productDoc.data().sellerId !== auth.currentUser.uid) {
        throw new Error('You can only modify your own products');
      }

      // Toggle the hidden status
      await updateDoc(productRef, {
        hidden: !productDoc.data().hidden,
        updatedAt: serverTimestamp()
      });

      return !productDoc.data().hidden; // Return new status
    } catch (error) {
      console.error('Error toggling product visibility:', error);
      throw new Error('Failed to update product visibility');
    }
  }

  static async updateProductsWithLowerCase() {
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const batch = writeBatch(db);
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.titleLower && data.title) {
          batch.update(doc.ref, {
            titleLower: data.title.toLowerCase()
          });
        }
      });

      await batch.commit();
      console.log('Updated all products with lowercase titles');
    } catch (error) {
      console.error('Error updating products:', error);
      throw error;
    }
  }

  static async getSellerInfo(sellerId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', sellerId));
      if (!userDoc.exists()) {
        throw new Error('Seller not found');
      }
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      console.error('Error getting seller info:', error);
      throw new Error('Failed to get seller information');
    }
  }

  static async updateExistingProductsSearchFields() {
    try {
      const queryRef = collection(db, 'products');
      const snapshot = await getDocs(queryRef);

      console.log(`Found ${snapshot.docs.length} products to update`);

      let batch = writeBatch(db);
      let operationsCount = 0;
      let totalUpdated = 0;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        // Check if the document needs updating
        if (!data.searchIndex) {
          // Generate searchable text from all relevant fields
          const searchableText = [
            data.title || '',
            data.description || '',
            data.brand || '',
            data.model || '',
            data.category || '',
            data.condition || '',
            data.location || ''
          ].filter(Boolean).join(' ');

          console.log(`Processing product: ${data.title}`, {
            id: doc.id,
            currentFields: {
              title: data.title,
              titleLower: data.titleLower,
              category: data.category
            }
          });

          // Generate search fields
          const searchIndex = generateSearchTerms(searchableText);
          const titleLower = (data.title || '').toLowerCase();
          const categoryLower = (data.category || '').toLowerCase();

          console.log('Generated fields:', {
            searchIndex,
            titleLower,
            categoryLower
          });

          // Update document
          batch.update(doc.ref, {
            titleLower,
            categoryLower,
            searchIndex,
            updatedAt: serverTimestamp()
          });

          operationsCount++;
          totalUpdated++;

          // Commit batch when it reaches limit
          if (operationsCount === 499) {
            console.log('Committing batch of 499 operations');
            await batch.commit();
            batch = writeBatch(db);
            operationsCount = 0;
          }
        }
      }

      // Commit any remaining operations
      if (operationsCount > 0) {
        console.log(`Committing final batch of ${operationsCount} operations`);
        await batch.commit();
      }

      console.log(`Successfully updated ${totalUpdated} products`);
      return totalUpdated;

    } catch (error) {
      console.error('Error updating products:', error);
      throw new Error('Failed to update products search fields');
    }
  }
}

// Helper function for generating search terms
function generateSearchTerms(text) {
  // Split text into words and normalize
  const words = text.toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove special characters
    .split(/\s+/)
    .filter(word => word.length >= 2); // Only keep words with 2 or more characters

  const terms = new Set();
  
  words.forEach(word => {
    // Add full word
    terms.add(word);
    
    // Add partial matches (minimum 3 characters)
    if (word.length > 3) {
      for (let i = 3; i <= word.length; i++) {
        terms.add(word.substring(0, i));
      }
    }
  });
  
  return Array.from(terms);
} 